import React, { useCallback, useState } from "react";
import { Award, Bell, Check } from "lucide-react";
import { cn } from "lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { Button } from "../ui/button";
import { useSocial } from "../../contexts/SocialContext";
import { useAuth } from "../../contexts/AuthContext";
import { timeAgo } from "../../utils/time";

/**
 * Header bell: unread count, and the list behind it.
 *
 * Sits beside the device dot because it is the same kind of thing — a quiet
 * status light that only asks for attention when it has something. Opening it
 * marks everything read, which is the honest reading of "I have seen these";
 * the entries stay so the panel remains a short history rather than an inbox
 * that empties itself as you look at it.
 */
const NotificationBell = ({ onNavigate }) => {
  const { user } = useAuth();
  const {
    notifications,
    unreadCount,
    loadNotifications,
    markNotificationsRead,
  } = useSocial();
  const [open, setOpen] = useState(false);

  const handleOpenChange = useCallback(
    (next) => {
      setOpen(next);
      if (!next) return;
      loadNotifications();
      if (unreadCount > 0) markNotificationsRead();
    },
    [loadNotifications, markNotificationsRead, unreadCount],
  );

  const handleClick = useCallback(
    (notification) => {
      setOpen(false);
      onNavigate?.(notification.target || "feed");
    },
    [onNavigate],
  );

  if (!user) return null;

  const hasUnread = unreadCount > 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={
            hasUnread ? `Notifications, ${unreadCount} unread` : "Notifications"
          }
          className="relative p-1.5 rounded-md text-muted-foreground hover:text-foreground transition-colors"
        >
          <Bell className="h-4 w-4" />
          {hasUnread && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[15px] h-[15px] px-1 rounded-full bg-primary text-primary-foreground text-[9px] font-bold leading-[15px] text-center tabular-nums"
              aria-hidden
            >
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80 p-0">
        <div className="px-3 py-2 border-b border-border">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Notifications
          </p>
        </div>

        {notifications.length === 0 ? (
          <div className="px-3 py-6 text-center">
            <p className="text-sm text-muted-foreground">Nothing yet.</p>
            <p className="text-xs text-muted-foreground/70 mt-1">
              Likes, comments, mate requests, training partners and badges
              turn up here.
            </p>
          </div>
        ) : (
          <ul className="max-h-80 overflow-y-auto divide-y divide-border/60">
            {notifications.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => handleClick(n)}
                  className={cn(
                    "w-full flex items-start gap-2.5 px-3 py-2.5 text-left hover:bg-foreground/5 transition-colors",
                    !n.read_at && "bg-primary/5",
                  )}
                >
                  {/* Nobody caused a badge, so it gets a medal, not a face. */}
                  {n.type === "badges_earned" || n.type === "challenge_completed" ? (
                    <div className="h-7 w-7 rounded-full bg-yellow-400/15 text-yellow-300 flex items-center justify-center flex-shrink-0">
                      <Award className="h-4 w-4" />
                    </div>
                  ) : n.actor_photo ? (
                    <img
                      src={n.actor_photo}
                      alt=""
                      className="h-7 w-7 rounded-full flex-shrink-0"
                    />
                  ) : (
                    <div className="h-7 w-7 rounded-full bg-primary/10 flex items-center justify-center text-[11px] font-medium flex-shrink-0">
                      {n.actor_name?.[0] || "?"}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm leading-snug">{n.message}</p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {timeAgo(n.created_at)}
                    </p>
                  </div>
                  {!n.read_at && (
                    <span
                      className="mt-1.5 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0"
                      aria-hidden
                    />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}

        {notifications.length > 0 && (
          <div className="px-2 py-1.5 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs text-muted-foreground"
              onClick={() => {
                setOpen(false);
                onNavigate?.("feed");
              }}
            >
              <Check className="h-3.5 w-3.5 mr-1.5" />
              Open Social
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default NotificationBell;
